/**
 * @fileoverview ClubOS Youth Academy — Attendance
 *
 * Players tab: tap-to-cycle card grid (Present ↔ Absent). Auto-save 4 s debounce.
 * Coaches tab: same pattern for coach attendance.
 * History tab: grouped by date with per-day summary + PDF download.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import PropTypes from "prop-types";
import { academyService } from "api";
import FootballLoader from "components/FootballLoader";

// ── Status config — only Present / Absent ────────────────────────────────────
const CYCLE = ["Present", "Absent"];
const NEXT  = { Present: "Absent", Absent: "Present" };

const STATUS_STYLE = {
  Present: { bg: "#e8f5e9", border: "#66bb6a", text: "#2e7d32", icon: "check_circle",  badge: "success" },
  Absent:  { bg: "#ffebee", border: "#ef5350", text: "#c62828", icon: "cancel",         badge: "error"   },
};

// ── Initials avatar ────────────────────────────────────────────────────────────
function Avatar({ name, status }) {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.Present;
  return (
    <SoftBox width={48} height={48} borderRadius="50%"
      display="flex" alignItems="center" justifyContent="center"
      sx={{ backgroundColor: s.border, flexShrink: 0 }}>
      <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#fff", fontSize: "1rem" }}>
        {initials}
      </SoftTypography>
    </SoftBox>
  );
}
Avatar.propTypes = { name: PropTypes.string.isRequired, status: PropTypes.string.isRequired };

// ── Attendance card (players or coaches) ──────────────────────────────────────
function AttendanceCard({ name, subtitle, status, onCycle, onSetStatus }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.Present;
  return (
    <SoftBox onClick={onCycle}
      sx={{
        backgroundColor: s.bg, border: `2px solid ${s.border}`,
        borderRadius: "12px", p: 1.5, cursor: "pointer", userSelect: "none",
        transition: "all 0.15s ease",
        "&:hover": { filter: "brightness(0.96)", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
        "&:active": { transform: "translateY(0)", filter: "brightness(0.92)" },
      }}>
      <SoftBox display="flex" alignItems="center" gap={1.5} mb={1.25}>
        <Avatar name={name} status={status} />
        <SoftBox flex={1} minWidth={0}>
          <SoftTypography variant="button" fontWeight="bold"
            sx={{ color: s.text, display: "block", lineHeight: 1.3, whiteSpace: "nowrap" }}>
            {name}
          </SoftTypography>
          <SoftTypography variant="caption" sx={{ color: s.text, opacity: 0.7 }}>{subtitle}</SoftTypography>
        </SoftBox>
      </SoftBox>
      <SoftBox display="flex" alignItems="center" justifyContent="space-between">
        <SoftBox display="flex" alignItems="center" gap={0.5}>
          <Icon sx={{ fontSize: "0.85rem", color: s.border }}>{s.icon}</Icon>
          <SoftTypography variant="caption" fontWeight="bold" sx={{ color: s.text }}>{status}</SoftTypography>
        </SoftBox>
        <SoftBox display="flex" gap={0.5}>
          {CYCLE.map((st) => (
            <Tooltip key={st} title={st} placement="top">
              <SoftBox onClick={(e) => { e.stopPropagation(); onSetStatus(st); }}
                sx={{
                  width: 10, height: 10, borderRadius: "50%",
                  backgroundColor: STATUS_STYLE[st].border,
                  opacity: status === st ? 1 : 0.3, cursor: "pointer",
                  transition: "opacity 0.15s", "&:hover": { opacity: 0.8 },
                }} />
            </Tooltip>
          ))}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}
AttendanceCard.propTypes = {
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onCycle: PropTypes.func.isRequired,
  onSetStatus: PropTypes.func.isRequired,
};

// ── Stats bar ──────────────────────────────────────────────────────────────────
function StatsBar({ items, records }) {
  const counts = { Present: 0, Absent: 0 };
  items.forEach((p) => { const s = records[p.id] ?? "Present"; if (counts[s] != null) counts[s]++; });
  const total = items.length;
  const pct   = total > 0 ? Math.round((counts.Present / total) * 100) : 0;
  return (
    <SoftBox display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
      {CYCLE.map((s) => {
        const style = STATUS_STYLE[s];
        return (
          <SoftBox key={s} display="flex" alignItems="center" gap={0.75}
            sx={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, borderRadius: "8px", px: 1.25, py: 0.5 }}>
            <Icon sx={{ fontSize: "0.85rem", color: style.border }}>{style.icon}</Icon>
            <SoftTypography variant="caption" fontWeight="bold" sx={{ color: style.text }}>
              {counts[s]} {s}
            </SoftTypography>
          </SoftBox>
        );
      })}
      <SoftBox sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
        <SoftTypography variant="body2" fontWeight="bold" color="dark">{pct}%</SoftTypography>
        <SoftTypography variant="caption" color="text">attendance</SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}
StatsBar.propTypes = { items: PropTypes.array.isRequired, records: PropTypes.object.isRequired };

// ── PDF export ─────────────────────────────────────────────────────────────────
async function downloadPDF(historyRecords, batchName, filterStatus, dateFrom, dateTo) {
  const { jsPDF } = await import("jspdf");
  const autoTable  = (await import("jspdf-autotable")).default;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16); doc.text("Attendance Report", 14, 18);
  doc.setFontSize(10); doc.setTextColor(100);
  doc.text(`Generated: ${now}`, 14, 25);
  doc.text(`Batch: ${batchName} | Status: ${filterStatus || "All"} | ${dateFrom || "—"} → ${dateTo || "—"}`, 14, 30);

  const rows = historyRecords.filter((r) => {
    if (filterStatus && r.attendanceStatus !== filterStatus) return false;
    if (dateFrom && r.attendanceDate < dateFrom) return false;
    if (dateTo && r.attendanceDate > dateTo) return false;
    return true;
  });

  const cnt = { Present: 0, Absent: 0 };
  rows.forEach((r) => { if (cnt[r.attendanceStatus] != null) cnt[r.attendanceStatus]++; });
  doc.setFontSize(11); doc.setTextColor(0);
  doc.text(`Total: ${rows.length}  |  Present: ${cnt.Present}  |  Absent: ${cnt.Absent}`, 14, 36);

  autoTable(doc, {
    startY: 42,
    head: [["#", "Player", "Date", "Status"]],
    body: rows.map((r, i) => [i + 1, r.playerName, r.attendanceDate, r.attendanceStatus]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 248, 255] },
  });

  doc.save(`attendance-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── History view (players) ─────────────────────────────────────────────────────
function HistoryView({ records, loading, selectedBatch, filterStatus, dateFrom, dateTo,
                       onFilterStatus, onDateFrom, onDateTo, onDownload }) {
  const filtered = records.filter((r) => {
    if (filterStatus && r.attendanceStatus !== filterStatus) return false;
    if (dateFrom && r.attendanceDate < dateFrom) return false;
    if (dateTo && r.attendanceDate > dateTo) return false;
    return true;
  });
  const byDate = filtered.reduce((acc, r) => { (acc[r.attendanceDate] = acc[r.attendanceDate] || []).push(r); return acc; }, {});
  const dates  = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField select size="small" label="Status" value={filterStatus}
            onChange={(e) => onFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All Status</MenuItem>
            {CYCLE.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField size="small" label="From Date" type="date" value={dateFrom}
            onChange={(e) => onDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="To Date" type="date" value={dateTo}
            onChange={(e) => onDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <SoftButton variant="outlined" color="info" size="small" startIcon={<Icon>download</Icon>}
            onClick={onDownload} disabled={records.length === 0}>
            Download PDF
          </SoftButton>
          <SoftTypography variant="caption" color="text" sx={{ ml: "auto" }}>
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
          </SoftTypography>
        </SoftBox>
      </Card>

      {loading ? (
        <Card><FootballLoader label="Loading attendance…" /></Card>
      ) : !selectedBatch ? (
        <Card><SoftBox p={5} textAlign="center">
          <Icon sx={{ fontSize: "2.5rem", color: "#ccc", display: "block", mb: 1 }}>history</Icon>
          <SoftTypography variant="body2" color="text">Select a batch to view history</SoftTypography>
        </SoftBox></Card>
      ) : dates.length === 0 ? (
        <Card><SoftBox p={4} textAlign="center">
          <SoftTypography variant="caption" color="text">No records match the filters</SoftTypography>
        </SoftBox></Card>
      ) : (
        dates.map((date) => {
          const dayRecords = byDate[date];
          const cnt = { Present: 0, Absent: 0 };
          dayRecords.forEach((r) => { if (cnt[r.attendanceStatus] != null) cnt[r.attendanceStatus]++; });
          const dayPct = dayRecords.length > 0 ? Math.round((cnt.Present / dayRecords.length) * 100) : 0;
          const dateLabel = new Date(date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

          return (
            <Card key={date} sx={{ mb: 2 }}>
              <SoftBox px={2.5} py={1.5} display="flex" alignItems="center" flexWrap="wrap" gap={1}
                sx={{ borderBottom: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
                <Icon sx={{ fontSize: "1rem", color: "#7b809a" }}>calendar_today</Icon>
                <SoftTypography variant="button" fontWeight="bold" color="dark">{dateLabel}</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" flexWrap="wrap" p={1.5} gap={1}>
                {dayRecords.map((r) => {
                  const s = STATUS_STYLE[r.attendanceStatus] ?? STATUS_STYLE.Present;
                  return (
                    <SoftBox key={r.id} display="flex" alignItems="center" gap={0.75}
                      sx={{ backgroundColor: s.bg, border: `1px solid ${s.border}`,
                            borderRadius: "8px", px: 1.25, py: 0.5, minWidth: 140 }}>
                      <Icon sx={{ fontSize: "0.8rem", color: s.border }}>{s.icon}</Icon>
                      <SoftTypography variant="caption" fontWeight="medium" sx={{ color: s.text }}>
                        {r.playerName}
                      </SoftTypography>
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </Card>
          );
        })
      )}
    </>
  );
}
HistoryView.propTypes = {
  records: PropTypes.array.isRequired, loading: PropTypes.bool.isRequired,
  selectedBatch: PropTypes.string.isRequired, filterStatus: PropTypes.string.isRequired,
  dateFrom: PropTypes.string.isRequired, dateTo: PropTypes.string.isRequired,
  onFilterStatus: PropTypes.func.isRequired, onDateFrom: PropTypes.func.isRequired,
  onDateTo: PropTypes.func.isRequired, onDownload: PropTypes.func.isRequired,
};

// ── Coach attendance tab ───────────────────────────────────────────────────────
function CoachAttendanceTab({ coaches, selectedDate, onDateChange, showSnack }) {
  const [records, setRecords]   = useState({});
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [dirty, setDirty]       = useState(false);
  const [search, setSearch]     = useState("");
  const autoTimer = useRef(null);

  // Load existing records whenever date changes
  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    academyService.getCoachAttendance({ date: selectedDate })
      .then((d) => {
        const map = {};
        (Array.isArray(d) ? d : []).forEach((r) => { map[r.coachId] = r.attendanceStatus; });
        coaches.forEach((c) => { if (!map[c.id]) map[c.id] = "Present"; });
        setRecords(map);
      })
      .catch(() => {
        const map = {};
        coaches.forEach((c) => { map[c.id] = "Present"; });
        setRecords(map);
      })
      .finally(() => setLoading(false));
  }, [selectedDate, coaches]);

  const doSave = useCallback(async (rec, date, auto = false) => {
    if (!date || coaches.length === 0) return;
    setSaving(true);
    try {
      const entries = coaches.map((c) => ({ coachId: c.id, status: rec[c.id] ?? "Present", notes: null }));
      const tenantId = coaches[0]?.tenantId ?? null;
      await academyService.bulkMarkCoachAttendance({ date, tenantId, entries });
      setDirty(false);
      showSnack(auto ? "Auto-saved." : `Coach attendance saved for ${coaches.length} coaches.`);
    } catch {
      showSnack("Save failed — please try again.", "error");
    } finally { setSaving(false); }
  }, [coaches, showSnack]);

  const setStatus = useCallback((coachId, status) => {
    setRecords((prev) => {
      const next = { ...prev, [coachId]: status };
      if (autoTimer.current) clearTimeout(autoTimer.current);
      autoTimer.current = setTimeout(() => doSave(next, selectedDate, true), 4000);
      return next;
    });
    setDirty(true);
  }, [doSave, selectedDate]);

  const cycleStatus = (coachId) => setStatus(coachId, NEXT[records[coachId] ?? "Present"]);
  const markAll = (status) => {
    const next = {};
    coaches.forEach((c) => { next[c.id] = status; });
    setRecords(next); setDirty(true);
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = setTimeout(() => doSave(next, selectedDate, true), 4000);
  };

  const visible = coaches.filter((c) => !search || c.fullName.toLowerCase().includes(search.toLowerCase()));
  const counts  = { Present: 0, Absent: 0 };
  coaches.forEach((c) => { const s = records[c.id] ?? "Present"; if (counts[s] != null) counts[s]++; });

  if (loading) return <Card><FootballLoader label="Loading coaches…" /></Card>;

  return (
    <>
      {/* Toolbar */}
      <Card sx={{ mb: 2 }}>
        <SoftBox p={1.5} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField size="small" placeholder="Search coach…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Icon fontSize="small">search</Icon></InputAdornment> }} />
          <Divider orientation="vertical" flexItem />
          <SoftTypography variant="caption" color="text" fontWeight="medium">Mark all:</SoftTypography>
          {CYCLE.map((s) => (
            <SoftBox key={s} onClick={() => markAll(s)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                backgroundColor: STATUS_STYLE[s].bg, border: `1.5px solid ${STATUS_STYLE[s].border}`,
                borderRadius: "8px", px: 1.25, py: 0.5, cursor: "pointer",
                "&:hover": { filter: "brightness(0.94)" },
              }}>
              <Icon sx={{ fontSize: "0.8rem", color: STATUS_STYLE[s].border }}>{STATUS_STYLE[s].icon}</Icon>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ color: STATUS_STYLE[s].text }}>{s}</SoftTypography>
            </SoftBox>
          ))}
          <SoftBox ml="auto" display="flex" alignItems="center" gap={1}>
            {dirty && !saving && (
              <SoftTypography variant="caption" color="warning" sx={{ fontStyle: "italic" }}>Unsaved changes</SoftTypography>
            )}
            <SoftButton variant="gradient" color="info" size="small" startIcon={<Icon>save</Icon>}
              onClick={() => doSave(records, selectedDate)} disabled={saving || coaches.length === 0}>
              Save
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Card>

      {/* Stats */}
      {coaches.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <SoftBox p={1.5}><StatsBar items={coaches} records={records} /></SoftBox>
        </Card>
      )}

      {coaches.length === 0 ? (
        <Card><SoftBox p={4} textAlign="center">
          <SoftTypography variant="caption" color="text">No active coaches found</SoftTypography>
        </SoftBox></Card>
      ) : (
        <>
          <SoftTypography variant="caption" color="text" display="block" mb={1.5} ml={0.5}>
            {visible.length} of {coaches.length} coach{coaches.length !== 1 ? "es" : ""} shown
          </SoftTypography>
          <Grid container spacing={1.5}>
            {visible.map((c) => (
              <Grid key={c.id} item xs={6} sm={4} md={3} lg={2}>
                <AttendanceCard
                  name={c.fullName} subtitle={c.role}
                  status={records[c.id] ?? "Present"}
                  onCycle={() => cycleStatus(c.id)}
                  onSetStatus={(s) => setStatus(c.id, s)}
                />
              </Grid>
            ))}
          </Grid>
          <SoftBox mt={2} display="flex" justifyContent="flex-end">
            <SoftButton variant="gradient" color="info" startIcon={<Icon>save</Icon>}
              onClick={() => doSave(records, selectedDate)} disabled={saving || coaches.length === 0}>
              {saving ? "Saving…" : `Save Attendance (${coaches.length} coaches)`}
            </SoftButton>
          </SoftBox>
        </>
      )}
    </>
  );
}
CoachAttendanceTab.propTypes = {
  coaches: PropTypes.array.isRequired,
  selectedDate: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  showSnack: PropTypes.func.isRequired,
};

// ── Main ──────────────────────────────────────────────────────────────────────
function AcademyAttendance() {
  const [batches, setBatches]             = useState([]);
  const [players, setPlayers]             = useState([]);
  const [coaches, setCoaches]             = useState([]);
  const [records, setRecords]             = useState({});
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedDate, setSelectedDate]   = useState(new Date().toISOString().slice(0, 10));
  const [tab, setTab]                     = useState("players");
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [dirty, setDirty]                 = useState(false);
  const [searchTerm, setSearchTerm]       = useState("");
  const [filterOnly, setFilterOnly]       = useState("");

  // History
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filterStatus, setFilterStatus]     = useState("");
  const [dateFrom, setDateFrom]             = useState("");
  const [dateTo, setDateTo]                 = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const showSnack = useCallback((msg, sev = "success") => setSnack({ open: true, msg, sev }), []);
  const autoSaveTimer = useRef(null);

  // Load batches + coaches on mount
  useEffect(() => {
    academyService.getBatches({ status: "Active" })
      .then((d) => setBatches(Array.isArray(d) ? d : []))
      .catch(() => setBatches([]));
    academyService.getCoaches({ status: "Active" })
      .then((d) => setCoaches(Array.isArray(d) ? d : []))
      .catch(() => setCoaches([]));
  }, []);

  // Load players + existing player attendance
  useEffect(() => {
    if (!selectedBatch) { setPlayers([]); setRecords({}); return; }
    setLoading(true); setDirty(false);
    Promise.all([
      academyService.getPlayers({ batchId: selectedBatch, status: "Active" }),
      academyService.getAttendance({ batchId: selectedBatch, date: selectedDate }),
    ])
      .then(([ps, att]) => {
        const pl  = Array.isArray(ps)  ? ps  : [];
        const att2 = Array.isArray(att) ? att : [];
        setPlayers(pl);
        const map = {};
        att2.forEach((a) => { map[a.playerId] = a.attendanceStatus; });
        pl.forEach((p) => { if (!map[p.id]) map[p.id] = "Absent"; });
        setRecords(map);
      })
      .catch(() => { setPlayers([]); setRecords({}); })
      .finally(() => setLoading(false));
  }, [selectedBatch, selectedDate]);

  // Load player history
  const loadHistory = useCallback(() => {
    if (!selectedBatch) return;
    setHistoryLoading(true);
    academyService.getAttendance({ batchId: selectedBatch })
      .then((d) => setHistoryRecords(Array.isArray(d) ? d : []))
      .catch(() => setHistoryRecords([]))
      .finally(() => setHistoryLoading(false));
  }, [selectedBatch]);

  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  // Player save
  const doSave = useCallback(async (pl, rec, batch, date, auto = false) => {
    if (!batch || pl.length === 0) return;
    setSaving(true);
    try {
      const entries = pl.map((p) => ({ playerId: p.id, status: rec[p.id] ?? "Present", notes: null }));
      await academyService.bulkMarkAttendance({ batchId: batch, date, tenantId: pl[0]?.tenantId ?? null, entries });
      setDirty(false);
      showSnack(auto ? "Auto-saved." : `Attendance saved for ${pl.length} player${pl.length !== 1 ? "s" : ""}.`);
    } catch {
      showSnack("Save failed — please try again.", "error");
    } finally { setSaving(false); }
  }, [showSnack]);

  const setStatus = useCallback((playerId, status) => {
    setRecords((prev) => {
      const next = { ...prev, [playerId]: status };
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(() => {
        setPlayers((pl) => {
          setSelectedBatch((batch) => {
            setSelectedDate((date) => { doSave(pl, next, batch, date, true); return date; });
            return batch;
          });
          return pl;
        });
      }, 4000);
      return next;
    });
    setDirty(true);
  }, [doSave]);

  const cycleStatus = (playerId) => setStatus(playerId, NEXT[records[playerId] ?? "Present"]);
  const markAll = (status) => {
    const next = {};
    players.forEach((p) => { next[p.id] = status; });
    setRecords(next); setDirty(true);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => doSave(players, next, selectedBatch, selectedDate, true), 4000);
  };

  const visiblePlayers = players.filter((p) => {
    if (searchTerm && !p.fullName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterOnly && (records[p.id] ?? "Present") !== filterOnly) return false;
    return true;
  });

  const batchName = batches.find((b) => b.id === selectedBatch)?.name ?? "All Batches";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={0}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">Attendance</SoftTypography>
          <SoftTypography variant="body2" color="text">
            Tap a card to toggle status · dots for direct selection
          </SoftTypography>
        </SoftBox>

        <SoftBox sx={{ borderBottom: "2px solid #f0f0f0", mb: 3, mt: 1.5 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 44,
              "& .MuiTabs-indicator": { backgroundColor: "#1a73e8", height: 2 },
              "& .MuiTab-root": {
                minHeight: 44, textTransform: "none", fontWeight: 500,
                fontSize: "0.875rem", color: "#7b809a", px: 2,
                "&.Mui-selected": { color: "#1a73e8", fontWeight: 700 },
              },
            }}>
            <Tab value="players"  icon={<Icon sx={{ fontSize: "1rem !important" }}>groups</Icon>}         iconPosition="start" label="Players"    disableRipple />
            <Tab value="coaches"  icon={<Icon sx={{ fontSize: "1rem !important" }}>manage_accounts</Icon>} iconPosition="start" label="Coaches"    disableRipple />
            <Tab value="history"  icon={<Icon sx={{ fontSize: "1rem !important" }}>history</Icon>}         iconPosition="start" label="History"    disableRipple />
          </Tabs>
        </SoftBox>

        {/* Date + Batch selector */}
        <Card sx={{ mb: 2 }}>
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            {(tab === "players" || tab === "history") && (
              <TextField select size="small" label="Batch" value={selectedBatch}
                onChange={(e) => { setSelectedBatch(e.target.value); setFilterOnly(""); setSearchTerm(""); }}
                sx={{ minWidth: 240 }}>
                <MenuItem value="">— Select Batch —</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name} ({b.ageGroup})</MenuItem>
                ))}
              </TextField>
            )}

            {(tab === "players" || tab === "coaches") && (
              <TextField size="small" label="Date" type="date" value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
            )}
          </SoftBox>
        </Card>

        {/* ── PLAYERS TAB ── */}
        {tab === "players" && (
          <>
            {players.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <SoftBox p={1.5} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                   <SoftTypography variant="caption" color="text" fontWeight="medium">Show:</SoftTypography>
                   {["Absent", "Present"].map((s) => (
                     <Chip key={s} label={s} size="small" clickable
                       onClick={() => setFilterOnly((f) => f === s ? "" : s)}
                       sx={{
                         backgroundColor: filterOnly === s ? STATUS_STYLE[s].border : STATUS_STYLE[s].bg,
                         color: filterOnly === s ? "#fff" : STATUS_STYLE[s].text,
                         fontWeight: 600, border: `1px solid ${STATUS_STYLE[s].border}`,
                       }} />
                   ))}
                   {filterOnly && <Chip label="Show all" size="small" clickable onClick={() => setFilterOnly("")} sx={{ fontWeight: 600 }} />}
                   <SoftBox ml="auto" display="flex" alignItems="center" gap={1.5}>
                     <TextField size="small" placeholder="Search player…" value={searchTerm}
                       onChange={(e) => setSearchTerm(e.target.value)} sx={{ minWidth: 200 }}
                       InputProps={{ startAdornment: <InputAdornment position="start"><Icon fontSize="small">search</Icon></InputAdornment> }} />
                     {dirty && !saving && (
                       <SoftTypography variant="caption" color="warning" sx={{ fontStyle: "italic" }}>Unsaved changes</SoftTypography>
                     )}
                     <SoftButton variant="gradient" color="info" size="small" startIcon={<Icon>save</Icon>}
                       onClick={() => doSave(players, records, selectedBatch, selectedDate)}
                       disabled={saving || !selectedBatch}>
                       Save
                     </SoftButton>
                   </SoftBox>
                </SoftBox>
              </Card>
            )}

            {loading ? (
              <Card><FootballLoader label="Loading players…" /></Card>
            ) : !selectedBatch ? (
              <Card>
                <SoftBox p={6} textAlign="center">
                  <Icon sx={{ fontSize: "3rem", color: "#ccc", display: "block", mb: 1.5 }}>how_to_reg</Icon>
                  <SoftTypography variant="h6" color="text">Select a batch to begin</SoftTypography>
                </SoftBox>
              </Card>
            ) : players.length === 0 ? (
              <Card><SoftBox p={4} textAlign="center">
                <SoftTypography variant="caption" color="text">No active players in this batch</SoftTypography>
              </SoftBox></Card>
            ) : visiblePlayers.length === 0 ? (
              <Card><SoftBox p={4} textAlign="center">
                <SoftTypography variant="caption" color="text">No players match the filter</SoftTypography>
              </SoftBox></Card>
            ) : (
              <>
                <SoftTypography variant="caption" color="text" display="block" mb={1.5} ml={0.5}>
                  {visiblePlayers.length} of {players.length} player{players.length !== 1 ? "s" : ""} shown
                  {filterOnly ? ` · showing ${filterOnly} only` : ""}
                </SoftTypography>
                <Grid container spacing={1.5}>
                  {visiblePlayers.map((p) => (
                    <Grid key={p.id} item xs={6} sm={4} md={3} lg={2}>
                      <AttendanceCard
                        name={p.fullName} subtitle={p.ageGroup}
                        status={records[p.id] ?? "Present"}
                        onCycle={() => cycleStatus(p.id)}
                        onSetStatus={(s) => setStatus(p.id, s)}
                      />
                    </Grid>
                  ))}
                </Grid>
                <SoftBox mt={2} display="flex" justifyContent="flex-end">
                  <SoftButton variant="gradient" color="info" startIcon={<Icon>save</Icon>}
                    onClick={() => doSave(players, records, selectedBatch, selectedDate)}
                    disabled={saving || !selectedBatch}>
                    {saving ? "Saving…" : `Save Attendance (${players.length} players)`}
                  </SoftButton>
                </SoftBox>
              </>
            )}
          </>
        )}

        {/* ── COACHES TAB ── */}
        {tab === "coaches" && (
          <CoachAttendanceTab
            coaches={coaches}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            showSnack={showSnack}
          />
        )}

        {/* ── HISTORY TAB ── */}
        {tab === "history" && (
          <HistoryView
            records={historyRecords} loading={historyLoading}
            selectedBatch={selectedBatch} filterStatus={filterStatus}
            dateFrom={dateFrom} dateTo={dateTo}
            onFilterStatus={setFilterStatus} onDateFrom={setDateFrom} onDateTo={setDateTo}
            onDownload={() => downloadPDF(historyRecords, batchName, filterStatus, dateFrom, dateTo)}
          />
        )}

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default AcademyAttendance;
